import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import RevisionNoteEditor from '../../components/tests/RevisionNoteEditor';
import revisionService from '../../services/RevisionService';
import { useAuth } from '../../contexts/AuthContext'; // adjust if you already expose user info elsewher
import { 
  Container, 
  Typography, 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  IconButton, 
  Button, 
  TextField,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Card,
  CardContent,
  Snackbar, Alert,
  FormControl, Select, MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  List,
  ListItem,
  ListItemSecondaryAction,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ArrowBack, CheckCircle, Cancel, Edit as EditIcon, RadioButtonUnchecked, RateReview, CloudUpload, ArrowDropDown, Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import testService from '../../services/testService';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch, FormControlLabel
} from '@mui/material';
import api from '../../services/api';
import UploadModal from '../../components/tests/UploadModal.jsx';
import PdfAnnotationViewer from '../../components/tests/PdfAnnotationViewer.jsx';
import uploadService from '../../services/uploadService';
import MathRenderer from '../../components/common/MathRenderer';
import { 
  ANSWERS, 
  ATTEMPTS, 
  REVISIONS, 
  TEST_ATTEMPT_NOTE 
} from '../../config/Config';
import { ALLOWED_FILE_TYPES, MAX_FILE_SIZE_MB } from '../../config/Config';

// ------------------------------------------------------------------
// Helper to proxy image URLs (reuse same logic as RevisionNotesPage)
const proxyImgUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/exams/question-image-proxy/') || url.includes('/media/') || url.includes('/static/')) return url;
  try {
    const backend = new URL(api.defaults.baseURL || '', window.location.origin);
    const imgUrl = new URL(url, backend);
    if (imgUrl.host === window.location.host) return imgUrl.pathname + imgUrl.search;
  } catch {}
  return `/api/proxy/?url=${encodeURIComponent(url)}`;
};

// ------------------------------------------------------------------
// Helper viewers for teacher table
// ------------------------------------------------------------------
const StudentFlagViewer = ({ attemptId, questionId, revisionData }) => {
  const [flag, setFlag] = React.useState(null);
  
  React.useEffect(() => {
    if (revisionData && Array.isArray(revisionData)) {
      const note = revisionData.find((n) => n.question === questionId);
      setFlag(
        note?.student_note_category ||
        note?.student_flag ||
        note?.category ||
        '—'
      );
    } else {
      setFlag('—');
    }
  }, [revisionData, questionId]);

  return <Typography variant="body2">{flag}</Typography>;
};

const TeacherNoteViewer = ({ attemptId, questionId, revisionData }) => {
  const [open, setOpen] = React.useState(false);
  const [comment, setComment] = React.useState('');
  const [loading, setLoading] = React.useState(true);

  useEffect(() => {
    if (revisionData && Array.isArray(revisionData)) {
      const note = revisionData.find((n) => n.question === questionId);
      setComment(note?.teacher_comment || '');
      setLoading(false);
    } else {
      setComment('');
      setLoading(false);
    }
  }, [revisionData, questionId]);

  if (loading) return null;
  if (!comment) return <Typography variant="body2">—</Typography>;

  return (
    <>
      <IconButton size="small" onClick={() => setOpen(true)} aria-label="View comment">
        <CheckCircle fontSize="small" />
      </IconButton>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Teacher's Note</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{comment}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} size="small">Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const RecheckViewer = ({ attemptId, questionId, revisionData }) => {
  const [rechecked, setRechecked] = React.useState(null);
  
  React.useEffect(() => {
    if (revisionData && Array.isArray(revisionData)) {
      const note = revisionData.find((n) => n.question === questionId);
      if (note) {
        setRechecked(note.rechecked || false);
      } else {
        setRechecked(false);
      }
    } else {
      setRechecked(false);
    }
  }, [revisionData, questionId]);

  if (rechecked === null) return null;
  
  return (
    <Chip 
      icon={rechecked ? <CheckCircle /> : <RadioButtonUnchecked />} 
      label={rechecked ? 'Rechecked' : 'Not rechecked'} 
      color={rechecked ? 'success' : 'default'} 
      size="small" 
    />
  );
};

const TestResultsPage = ({ attemptId: propAttemptId,onBack  }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth() || {};
  const isTeacher = user?.isTeacher || user?.is_teacher || user?.is_staff;

  const routeParams = useParams();
  const id = propAttemptId || routeParams.attemptId;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [testDetails, setTestDetails] = useState(null);
  const [revNotes, setRevNotes] = useState([]);
  const [revisionData, setRevisionData] = useState([]); // Store all revision data for all questions

  const [teacherNote, setTeacherNote] = useState('');
  const [isMarked, setIsMarked] = useState(false);
  const [noteId, setNoteId] = useState(null);
  const [noteLoading, setNoteLoading] = useState(false); // Start as false, only set to true when actually loading
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // 'success' or 'error'
  const [worksheetDialog, setWorksheetDialog] = useState({
    open: false,
    testId: null,
    studentId: null,
    teacherManagedStudentId: null,
    attemptId: null,
    questions: [],
    revisionData: [],
    studentWorksheetUiForManagedRoster: false,
  });
  const [reviewDialog, setReviewDialog] = useState({
    open: false,
    uploadId: null,
    filename: null,
    uploadSource: 'student',
  });
  const [uploadMenuAnchor, setUploadMenuAnchor] = useState(null);
  const [referenceUploadDialogOpen, setReferenceUploadDialogOpen] = useState(false);
  const [referenceUploadFile, setReferenceUploadFile] = useState(null);
  const [referenceUploading, setReferenceUploading] = useState(false);
  const [referenceUploads, setReferenceUploads] = useState([]);
  const [loadingReferenceUploads, setLoadingReferenceUploads] = useState(false);
  
  // Ref to track if we've already attempted to fetch teacher note
  const teacherNoteFetchedRef = React.useRef(false);
  // Ref to track if we've already attempted to fetch test details
  const testDetailsFetchedRef = React.useRef(false);
  // Ref to track debounce timeout for teacher note saving
  const saveTimeoutRef = React.useRef(null);
  
  // Refs for scroll synchronization
  const topScrollRef = useRef(null);
  const bottomScrollRef = useRef(null);
  
  // determine if recheck column should display
  const showRecheck = React.useMemo(() => {
    if (!results?.completed_at) return false;
    const completed = new Date(results.completed_at);
    const now = new Date();
    const diffMs = now - completed;
    return diffMs >= 24 * 60 * 60 * 1000;
  }, [results?.completed_at]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);
  
  // Scroll synchronization functions
  const handleTopScroll = (e) => {
    if (bottomScrollRef.current) {
      bottomScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
  };
  
  const handleBottomScroll = (e) => {
    if (topScrollRef.current) {
      topScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  // Memoize the questions array to prevent unnecessary re-renders
  const questions = React.useMemo(() => {
    if (!results?.questions) return [];
    return results.questions;
  }, [results?.questions]);

  // Flatten questions into result rows for table: main question + sub-questions (1, 1a, 1b, 1c, 2, ...)
  // questions are already sorted by id (same as PDF) when set in refreshResults
  const resultRows = React.useMemo(() => {
    if (!Array.isArray(questions) || questions.length === 0) return [];
    const rows = [];
    questions.forEach((q, idx) => {
      const qNo = idx + 1;
      const hasSub = q.has_subquestions && Array.isArray(q.sub_questions) && q.sub_questions.length > 0;
      if (hasSub) {
        // Add main question row first when there are sub-questions
        rows.push({
          questionNo: String(qNo),
          id: q.id,
          mainQuestionId: q.id,
          text: q.text,
          question_type: q.question_type,
          options: q.options ?? [],
          correct_answer: q.correct_answer ?? '',
          user_answer: q.user_answer ?? '', // Main question answer (usually empty for sub-question questions)
          confidence: q.confidence,
          marks_obtained: q.marks_obtained,
          isSub: false,
          isMainWithSubs: true, // Flag to indicate this is a main question with sub-questions
          question: q,
        });
        // Sub-questions already sorted by order in refreshResults; iterate in order
        q.sub_questions.forEach((sq) => {
          const subAns = (q.sub_answers || []).find((sa) => String(sa.sub_question_id ?? sa.sub_question) === String(sq.id));
          rows.push({
            questionNo: `${qNo}${sq.letter || ''}`,
            id: sq.id,
            mainQuestionId: q.id,
            text: sq.question_text ?? '',
            question_type: sq.type || q.question_type,
            options: sq.options ?? [],
            correct_answer: sq.correct_answer ?? '',
            user_answer: subAns?.answer ?? '',
            confidence: q.confidence,
            marks_obtained: subAns?.marks_obtained ?? null,
            isSub: true,
            question: q,
          });
        });
      } else {
        rows.push({
          questionNo: String(qNo),
          id: q.id,
          mainQuestionId: q.id,
          text: q.text,
          question_type: q.question_type,
          options: q.options ?? [],
          correct_answer: q.correct_answer ?? '',
          user_answer: q.user_answer ?? '',
          confidence: q.confidence,
          marks_obtained: q.marks_obtained,
          isSub: false,
          question: q,
        });
      }
    });
    return rows;
  }, [questions]);

  // All parts gradable in the UI must have a correct answer + a score/rubric (same rules as the PDF grading panel).
  // For questions with sub-parts (1a, 1b, …), grading lives on sub_answers — parent mark_percentage is often empty.
  const allQuestionsGraded = React.useMemo(() => {
    if (!questions || questions.length === 0) return false;
    const hasCorrectAnswer = (val) => {
      if (val == null) return false;
      const s = String(val).trim();
      return s !== '' && s.toLowerCase() !== 'n/a';
    };
    const binaryRubric = (v) => {
      if (v == null || v === '') return false;
      return v === 'correct' || v === 'incorrect' || v === '50' || v === '100';
    };
    // Only signals the TEACHER sets count as "graded": mark_percentage or a rubric decision
    // (set via the Review & Grade / PDF grading panel). marks_obtained is auto-stamped 0.00
    // by the auto-grader on submission, so it must NOT count — otherwise the finalize toggle
    // unlocks before the teacher has marked anything.
    const subAnswerHasGrade = (subAns) => {
      if (!subAns) return false;
      const mp = subAns.mark_percentage;
      if (mp != null && String(mp).trim() !== '') return true;
      if (binaryRubric(subAns.rubric_notation)) return true;
      if (binaryRubric(subAns.rubric_steps)) return true;
      return false;
    };
    const mainAnswerHasGrade = (q) => {
      const mp = q.mark_percentage;
      if (mp != null && String(mp).trim() !== '') return true;
      if (binaryRubric(q.rubric_notation)) return true;
      if (binaryRubric(q.rubric_steps)) return true;
      return false;
    };
    return questions.every((q) => {
      if (q.sub_questions?.length) {
        return q.sub_questions.every((sq) => {
          if (!hasCorrectAnswer(sq.correct_answer)) return false;
          const subAns = (q.sub_answers || []).find(
            (sa) => String(sa.sub_question_id ?? sa.sub_question) === String(sq.id)
          );
          return subAnswerHasGrade(subAns);
        });
      }
      return hasCorrectAnswer(q.correct_answer) && mainAnswerHasGrade(q);
    });
  }, [questions]);

  // Reset isMarked to false if questions become ungraded while marked complete.
  // Guard: only when questions have actually loaded — allQuestionsGraded is false while
  // the list is empty/loading, and firing then would silently revert a completed grading.
  React.useEffect(() => {
    if (isMarked && isTeacher && Array.isArray(questions) && questions.length > 0 && !allQuestionsGraded) {
      setIsMarked(false);
      // Use the current teacherNote value from state
      const currentNote = teacherNote;
      const payload = {
        test_attempt: id,
        teacher_note: currentNote,
        is_marked: false,
      };
      if (noteId) {
        api.put(TEST_ATTEMPT_NOTE, payload).catch(e => console.error('Error resetting grading complete:', e));
      } else {
        api.post(TEST_ATTEMPT_NOTE, payload)
          .then(({ data }) => setNoteId(data.id))
          .catch(e => console.error('Error resetting grading complete:', e));
      }
    }
  }, [allQuestionsGraded, isMarked, isTeacher, id, noteId, teacherNote, questions]);

  // Function to fetch all revision data once
  const fetchAllRevisionData = React.useCallback(async () => {
    try {
      const response = await api.get(REVISIONS);
      const revisionData = response.data;
      // Ensure revisionData is always an array
      const revisionArray = Array.isArray(revisionData) ? revisionData : (revisionData?.results || []);
      setRevisionData(revisionArray);
    } catch (err) {
      console.error('Error fetching revision data:', err);
      setRevisionData([]); // Set empty array on error
    }
  }, []);

  // Fetch attempt note: teachers get full note to edit; students get is_marked so Graded by Teacher pane shows when grading is done
  const fetchTeacherNote = React.useCallback(async () => {
    if (noteLoading) return;

    try {
      setNoteLoading(true);
      const res = await api.get(`${TEST_ATTEMPT_NOTE}?test_attempt=${id}`);
      const note = res.data;
      const marked = note.is_marked === true || note.is_marked === 'Yes' || String(note.is_marked).toLowerCase() === 'yes';
      setIsMarked(!!marked);

      if (isTeacher) {
        setNoteId(note.id);
        setTeacherNote(note.teacher_note || '');
      } else {
        setTeacherNote(note.teacher_note || '');
      }
    } catch (e) {
      console.error('Error loading teacher attempt note:', e);
      if (e.response?.status === 404) {
        setIsMarked(false);
        if (isTeacher) {
          setNoteId(null);
          setTeacherNote('');
        }
      }
    } finally {
      setNoteLoading(false);
    }
  }, [id, isTeacher, noteLoading]);

  // Reusable function to fetch and refresh results
  const refreshResults = React.useCallback(async (skipNavigationCheck = false) => {
    try {
      setLoading(true);
      
      // Fetch test results
      let resultsData;
      if(propAttemptId){
        // teacher view – access generic attempt detail endpoint
        const resp = await api.get(`${ATTEMPTS}${id}/`, { 
          params: { _t: Date.now() } // Cache busting for refresh
        });
        resultsData = resp.data;
      } else {
        resultsData = await testService.getTestResults(id);
      }
      
      if (!resultsData) {
        setError('No test results data found');
        return;
      }
      
      // Check if attempt is submitted (for student view) - skip during refresh after grading
      if (!skipNavigationCheck && !propAttemptId && (!resultsData.status || resultsData.status !== 'SUBMITTED')) {
        navigate('/tests', { replace: true });
        return;
      }
      
      // Build question rows array (include sub_questions and sub_answers for grading panel)
      // Order by question id to match PDF worksheet and test-taking sequence
      const mapped = (resultsData.answers || []).map((ans, index) => {
        const q = ans.question_details || {};
        const subQuestions = q.sub_questions ?? [];
        const sortedSubQuestions = [...subQuestions].sort((a, b) => {
          const orderA = a.order ?? a.order_number ?? 0;
          const orderB = b.order ?? b.order_number ?? 0;
          if (orderA !== orderB) return orderA - orderB;
          return (a.id ?? 0) - (b.id ?? 0);
        });
        const questionData = {
          id: q.id ?? ans.question_id,
          text: q.text ?? ans.question_text ?? 'Question text not available',
          question_type: q.type ?? ans.question_type,
          options: q.options ?? ans.question_options ?? [],
          correct_answer: q.correct_answer ?? ans.correct_answer ?? 'N/A',
          user_answer: ans.answer ?? ans.user_answer ?? '',
          marks: q.marks ?? ans.question_marks ?? 0,
          marks_obtained: ans.marks_obtained != null ? ans.marks_obtained : null,
          mark_percentage: ans.mark_percentage ?? null,
          rubric_notation: ans.rubric_notation ?? null,
          rubric_steps: ans.rubric_steps ?? null,
          confidence: ans.confidence ?? ans.confidence_level ?? null,
          feedback: ans.feedback || '',
          image_proxy_url: q.image_proxy_url || q.image_url || null,
          image_url: q.image_url || null,
          created_by: q.created_by ?? ans.question_created_by,
          has_subquestions: q.has_subquestions ?? false,
          sub_questions: sortedSubQuestions,
          sub_answers: ans.sub_answers ?? [],
          _sequence: index,
        };
        return questionData;
      });
      const questions = [...mapped].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));

      // Transform results for display
      const transformedResults = {
        ...resultsData,
        questions
      };
      
      setResults(transformedResults);
      setTestDetails(resultsData.test_details || resultsData.test);
      
      // Fetch attempt note for both teacher and student: teachers need full note to edit; students need is_marked to show Graded by Teacher pane
      if (!teacherNoteFetchedRef.current) {
        teacherNoteFetchedRef.current = true;
        fetchTeacherNote();
      }
      
      // Fetch all revision data once for all questions
      await fetchAllRevisionData();
      
    } catch (err) {
      console.error('Error fetching test results:', err);
      setError('Failed to load test results');
    } finally {
      setLoading(false);
    }
  }, [id, propAttemptId, navigate, fetchTeacherNote, fetchAllRevisionData]);

  useEffect(() => {
    refreshResults(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, propAttemptId]); // Only depend on id and propAttemptId to avoid infinite loops

  // Reset teacher note ref when attempt ID changes
  useEffect(() => {
    teacherNoteFetchedRef.current = false;
    testDetailsFetchedRef.current = false;
  }, [id]);

  // Fetch reference uploads when dialog opens
  useEffect(() => {
    const fetchReferenceUploads = async () => {
      if (!referenceUploadDialogOpen || !results) return;
      const sid = results?.student_id ?? results?.student;
      const tid = results?.test_id ?? results?.test;
      if (!sid || !tid) return;
      
      setLoadingReferenceUploads(true);
      try {
        const uploads = await uploadService.listUploads({ studentId: sid, testId: tid, role: 'teacher' });
        // Filter to show only reference files (those starting with reference_{testId}_)
        const referenceFiles = uploads.filter(u => 
          u.filename && u.filename.startsWith(`reference_${tid}_`)
        );
        setReferenceUploads(referenceFiles);
      } catch (err) {
        console.error('Failed to fetch reference uploads:', err);
      } finally {
        setLoadingReferenceUploads(false);
      }
    };
    fetchReferenceUploads();
  }, [referenceUploadDialogOpen, results]);

  

  // Save note (create or update) - now called automatically
  const saveTeacherNote = async () => {
    return saveTeacherNoteWithValue(teacherNote, isMarked);
  };

  const saveTeacherNoteWithValue = async (newNote, newMarked) => {
    try {
      const payload = {
        test_attempt: id,
        teacher_note: newNote,
        is_marked: newMarked,
      };

      if (noteId) {
        // Update existing note
        await api.put(TEST_ATTEMPT_NOTE, payload);
      } else {
        // Create new note
        const { data } = await api.post(TEST_ATTEMPT_NOTE, payload);
        setNoteId(data.id);
      }
      
      setSnackbarMessage('Note saved automatically');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (e) {
      console.error('Error saving teacher note:', e);
      setSnackbarMessage('Failed to save note');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };
  
  const renderAnswer = (question, answer) => {
    // Handle case where answer is an object with answer property
    const answerValue = answer?.answer || answer;
    
    if (!answerValue || (Array.isArray(answerValue) && answerValue.length === 0)) {
      return 'No answer provided';
    }
    
    // For multiple choice questions
    if (question.question_type === 'MULTIPLE' || question.question_type === 'multiple_choice') {
      // If options are available, try to find the matching option
      if (question.options && question.options.length > 0) {
        // Handle case where options is an array of objects with id and text
        if (typeof question.options[0] === 'object' && question.options[0] !== null) {
          const selectedOption = question.options.find(opt => 
            String(opt.id) === String(answerValue) || 
            (Array.isArray(answerValue) && answerValue.includes(String(opt.id)))
          );
          return selectedOption ? selectedOption.text : String(answerValue);
        } 
        // Handle case where options is an array of strings
        else if (Array.isArray(question.options)) {
          const selectedOption = question.options[answerValue];
          return selectedOption !== undefined ? selectedOption : String(answerValue);
        }
      }
      // If no options or couldn't find a match, just return the answer as is
      return String(answerValue);
    }
    
    // For true/false questions
    if (question.question_type === 'TRUE_FALSE' || question.question_type === 'true_false') {
      return String(answerValue).toLowerCase() === 'true' ? 'True' : 'False';
    }
    
    // For text answers
    return Array.isArray(answerValue) ? answerValue.join(', ') : String(answerValue);
  };

  const renderCorrectAnswer = (question) => {
    const corr = question.correct_answer;
    if (!corr || (Array.isArray(corr) && corr.length === 0)) return 'N/A';
    // Multiple choice
    if (question.question_type === 'MULTIPLE' || question.question_type === 'multiple_choice') {
      if (question.options && question.options.length > 0) {
        // options array objects
        if (typeof question.options[0] === 'object') {
          const texts = question.options
            .filter(opt => opt.text === corr)
            .map(opt => opt.text);
          return texts.join(', ');
        }
        // options array of strings
        return corr;
      }
      return corr;
    }
    // true/false
    if (question.question_type === 'TRUE_FALSE' || question.question_type === 'true_false') {
      return String(corr).toLowerCase() === 'true' ? 'True' : 'False';
    }
    // text or numeric
    return String(corr);
  };

  const normalize = (v) => String(v ?? '').trim().toLowerCase();

  const isAnswerCorrect = (question) => {
    const answerValue = question.user_answer;
    const correctAnswer = question.correct_answer;
    const corrAns = question.correct_answers ?? correctAnswer;
    const correctAnswers = Array.isArray(corrAns) ? corrAns : [corrAns];
    return Array.isArray(answerValue) 
      ? answerValue.length === correctAnswers.length && answerValue.every(ans => normalize(ans) === normalize(correctAnswers[0])) 
      : normalize(answerValue) === normalize(correctAnswer);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }


  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error" gutterBottom>
          {error}
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => navigate(-1)}
          startIcon={<ArrowBack />}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  if (!results) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          No results found for this test attempt.
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => onBack ? onBack() : navigate(-1)}
          startIcon={<ArrowBack />}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  // For students viewing own results, fallback to current user and test from details so Check Worksheet stays enabled
  const studentId = results?.student_id ?? results?.student ?? (!isTeacher && user?.id) ?? undefined;
  const teacherManagedStudentId =
    results?.teacher_managed_student_id ??
    (results?.teacher_managed_student && typeof results.teacher_managed_student === 'object'
      ? results.teacher_managed_student.id
      : results?.teacher_managed_student) ??
    undefined;
  const testId = results?.test_id ?? results?.test ?? testDetails?.id;
  const canOpenWorksheetReview =
    !!testId &&
    (!!studentId || (teacherManagedStudentId != null && String(teacherManagedStudentId).trim() !== ''));

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1.5, sm: 2 } }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: { xs: '1 1 100%', md: 1 }, minWidth: 0 }}>
          <IconButton onClick={() => onBack ? onBack() : navigate(-1)} aria-label="Go back" sx={{ mr: 0.5 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" component="h1" sx={{ flex: 1, textAlign: { xs: 'left', md: 'center' }, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
            {testDetails?.title ? `Test Result for ${testDetails.title}` : 'Test Results'}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        
        {testDetails && (
         <Grid container spacing={3} sx={{ mb: 4 }}>
         {/* Performance Overview */}
         <Grid item xs={12} md={7}>
           <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
             <CardContent sx={{ p: 3 }}>
               <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                 <Typography variant="h6" sx={{ fontWeight: 700 }}>Performance Overview</Typography>
                 <Chip
                   label={results.passed ? 'PASSED' : 'FAILED'}
                   color={results.passed ? 'success' : 'error'}
                   size="small"
                   sx={{ fontWeight: 700, letterSpacing: 0.5 }}
                 />
               </Box>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                 <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                   <CircularProgress variant="determinate" value={100} size={110} thickness={4} sx={{ color: 'grey.200' }} />
                   <CircularProgress
                     variant="determinate"
                     value={Math.max(0, Math.min(100, Number(results.score) || 0))}
                     size={110}
                     thickness={4}
                     sx={{ color: results.passed ? 'success.main' : 'error.main', position: 'absolute', left: 0 }}
                   />
                   <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                     <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1 }}>
                       {Math.round(Number(results.score) || 0)}%
                     </Typography>
                     <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1 }}>SCORE</Typography>
                   </Box>
                 </Box>
                 <Box sx={{ flex: 1, minWidth: 220 }}>
                   <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                     Assessment: <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{testDetails.title}</Box>
                   </Typography>
                   <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                     <Box>
                       <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Status</Typography>
                       <Typography variant="body2" sx={{ fontWeight: 600, color: isMarked ? 'success.main' : 'warning.main' }}>
                         {isMarked ? 'Graded' : 'Needs Review'}
                       </Typography>
                     </Box>
                     <Box>
                       <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Completed On</Typography>
                       <Typography variant="body2" sx={{ fontWeight: 600 }}>
                         {(() => {
                           const completedOn = results.completed_at || results.end_time;
                           return completedOn && !isNaN(new Date(completedOn).getTime())
                             ? new Date(completedOn).toLocaleDateString()
                             : '—';
                         })()}
                       </Typography>
                     </Box>
                     <Box>
                       <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Time Spent</Typography>
                       <Typography variant="body2" sx={{ fontWeight: 600 }}>{results.time_spent || '—'}</Typography>
                     </Box>
                   </Box>
                 </Box>
               </Box>
             </CardContent>
           </Card>
         </Grid>

         {/* Teacher Feedback */}
         <Grid item xs={12} md={5}>
  <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
    <CardContent sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }} gutterBottom>Teacher Feedback</Typography>
      {noteLoading ? (
        <CircularProgress size={18} />
      ) : isTeacher ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Teacher Note"
            placeholder="Add overall comments for the student…"
            value={teacherNote}
            onChange={(e) => {
              const newValue = e.target.value;
              setTeacherNote(newValue);
              
              // Clear existing timeout
              if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
              }
              
              // Set new timeout for debounced saving
              saveTimeoutRef.current = setTimeout(() => {
                saveTeacherNoteWithValue(newValue, isMarked);
              }, 1000); // Save after 1 second of no typing
            }}
            onBlur={() => {
              // Clear any pending timeout and save immediately
              if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
                saveTimeoutRef.current = null;
              }
              saveTeacherNoteWithValue(teacherNote, isMarked);
            }}
            multiline
            rows={3}
            inputProps={{ maxLength: 100 }}
            helperText={`${teacherNote.length}/100`}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Finalize grade</Typography>
              {!allQuestionsGraded && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  All questions must have a score and correct answer filled before marking as complete
                </Typography>
              )}
            </Box>
            <Tooltip 
              title={!allQuestionsGraded ? "Score and correct answer must be filled for every question before marking as complete." : ""}
              arrow
              placement="top"
            >
              <span>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isMarked}
                      onChange={(e) => {
                        const newMarkedValue = e.target.checked;
                        // Prevent setting to "Yes" if not all questions are graded
                        if (newMarkedValue && !allQuestionsGraded) {
                          setSnackbarMessage('Please fill score and correct answer for all questions before marking grading as complete');
                          setSnackbarSeverity('warning');
                          setSnackbarOpen(true);
                          return;
                        }
                        setIsMarked(newMarkedValue);
                        // Save immediately with the new value
                        saveTeacherNoteWithValue(teacherNote, newMarkedValue);
                      }}
                      disabled={!allQuestionsGraded}
                      color="success"
                    />
                  }
                  label={isMarked ? 'Yes' : 'No'}
                  labelPlacement="start"
                />
              </span>
            </Tooltip>
          </Box>
        </Box>
      ) : teacherNote ? (
        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
          {teacherNote}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">No teacher note provided.</Typography>
      )}
    </CardContent>
  </Card>
</Grid>

         </Grid>
       
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 2 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', md: '1.5rem' } }}>
            Question Analysis
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {isTeacher && teacherManagedStudentId != null && String(teacherManagedStudentId).trim() !== '' && testId && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloudUpload />}
                onClick={() => {
                  setWorksheetDialog({
                    open: true,
                    testId,
                    studentId: null,
                    teacherManagedStudentId,
                    attemptId: id,
                    questions: questions || [],
                    revisionData: revisionData || [],
                    studentWorksheetUiForManagedRoster: true,
                  });
                }}
                sx={{ minWidth: { xs: 'auto', sm: 100 }, borderRadius: 5 }}
              >
                Upload worksheet
              </Button>
            )}
            {isTeacher && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CloudUpload />}
                  endIcon={<ArrowDropDown />}
                  onClick={(e) => setUploadMenuAnchor(e.currentTarget)}
                  disabled={!canOpenWorksheetReview}
                  sx={{ minWidth: { xs: 'auto', sm: 100 }, borderRadius: 5 }}
                >
                  Upload
                </Button>
                <Menu
                  anchorEl={uploadMenuAnchor}
                  open={!!uploadMenuAnchor}
                  onClose={() => setUploadMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem
                    onClick={() => {
                      setUploadMenuAnchor(null);
                      if (studentId && testId) setReferenceUploadDialogOpen(true);
                    }}
                  >
                    <ListItemIcon><CloudUpload fontSize="small" /></ListItemIcon>
                    <ListItemText>Reference</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={<RateReview />}
              onClick={async () => {
                if (!testId || !canOpenWorksheetReview) return;
                try {
                  if (studentId) {
                    const uploads = await uploadService.listUploads({ studentId, testId, role: 'student' });
                    const pdfUpload = Array.isArray(uploads)
                      ? uploads.find((u) => (u.filename || '').toLowerCase().endsWith('.pdf'))
                      : null;
                    if (pdfUpload) {
                      setReviewDialog({
                        open: true,
                        uploadId: pdfUpload.id,
                        filename: pdfUpload.filename,
                        uploadSource: 'student',
                      });
                    } else {
                      setSnackbarMessage('No PDF uploaded. Student can upload a PDF worksheet.');
                      setSnackbarSeverity('warning');
                      setSnackbarOpen(true);
                    }
                    return;
                  }
                  const tm = teacherManagedStudentId;
                  const uploads = await uploadService.listUploads({
                    teacherManagedStudentId: tm,
                    testId,
                    role: 'teacher',
                  });
                  const pdfUpload = Array.isArray(uploads)
                    ? uploads.find((u) => (u.filename || '').toLowerCase().endsWith('.pdf'))
                    : null;
                  if (pdfUpload) {
                    setReviewDialog({
                      open: true,
                      uploadId: pdfUpload.id,
                      filename: pdfUpload.filename,
                      uploadSource: 'teacher',
                    });
                  } else {
                    setSnackbarMessage(
                      'No PDF worksheet found. Upload a worksheet from the assignments page first.'
                    );
                    setSnackbarSeverity('warning');
                    setSnackbarOpen(true);
                  }
                } catch (err) {
                  setSnackbarMessage(err?.message || 'Failed to load uploads');
                  setSnackbarSeverity('error');
                  setSnackbarOpen(true);
                }
              }}
              disabled={!canOpenWorksheetReview}
              sx={{ minWidth: { xs: 'auto', sm: 120 }, borderRadius: 5 }}
            >
              {isTeacher ? 'Review & Grade' : 'Check Worksheet'}
            </Button>
          </Box>
        </Box>

        {isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {resultRows?.map((row, index) => {
              const isMainWithSubs = row.isMainWithSubs;
              return (
                <Card key={row.isSub ? `sub-${row.mainQuestionId}-${row.id}` : row.id} variant="outlined" sx={{ overflow: 'hidden' }}>
                  <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                    <Typography variant="overline" color="primary" sx={{ fontWeight: 600 }}>Question {row.questionNo}</Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Question</Typography>
                      <MathRenderer content={row.text || ''} variant="body2" sx={{ wordBreak: 'break-word', lineHeight: 1.4 }} />
                      {!row.isSub && (row.question?.image_proxy_url || row.question?.image_url) && (
                        <Box mt={1}>
                          <img
                            src={proxyImgUrl(row.question.image_proxy_url || row.question.image_url)}
                            alt="question"
                            style={{ maxWidth: '100%', height: 'auto', borderRadius: 4 }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Your Answer</Typography>
                      <Typography variant="body2">
                        {isMainWithSubs ? 'See sub-questions' : <MathRenderer content={renderAnswer(row.question || { question_type: '', options: [] }, row.user_answer)} variant="body2" />}
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">Correct Answer</Typography>
                      <Typography variant="body2">
                        {isMainWithSubs ? 'See sub-questions' : <MathRenderer content={renderCorrectAnswer(row.question || { correct_answer: row.correct_answer, question_type: row.question_type, options: row.options })} variant="body2" />}
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Confident?</Typography>
                        <Typography variant="body2">{isMainWithSubs ? '—' : (row.confidence ?? row.question?.confidence_level ?? 'N/A')}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Score</Typography>
                        <Typography variant="body2">{isMainWithSubs ? '—' : (row.marks_obtained != null && row.marks_obtained !== '' ? String(row.marks_obtained) : '—')}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Student Flag</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {isTeacher ? (
                          <StudentFlagViewer attemptId={id} questionId={row.mainQuestionId} revisionData={revisionData} />
                        ) : (
                          <RevisionNoteEditor attemptId={id} questionId={row.mainQuestionId} userRole="STUDENT" mode="note" revisionData={revisionData} />
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        ) : (
          <>
        {/* Top scrollbar for better UX */}
        <Box 
          ref={topScrollRef}
          onScroll={handleTopScroll}
          sx={{ 
            overflowX: 'auto', 
            mb: 1,
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            backgroundColor: '#fafafa',
            '&::-webkit-scrollbar': { height: '8px' },
            '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: '4px' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: '4px', '&:hover': { backgroundColor: '#a8a8a8' } },
            scrollbarWidth: 'thin',
            scrollbarColor: '#c1c1c1 #f1f1f1',
          }}
        >
          <Table sx={{ tableLayout: 'fixed', visibility: 'hidden', minWidth: '1200px' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '5%' }}>#</TableCell>
                <TableCell sx={{ width: '25%' }}>Question</TableCell>
                <TableCell sx={{ width: '15%' }}>Your Answer</TableCell>
                <TableCell sx={{ width: '15%' }}>Correct Answer</TableCell>
                <TableCell sx={{ width: '12%' }}>Confident of the answer given?</TableCell>
                <TableCell sx={{ width: '10%' }}>Score</TableCell>
                <TableCell sx={{ width: '10%' }}>Student Flag</TableCell>
              </TableRow>
            </TableHead>
          </Table>
        </Box>

        <TableContainer 
          ref={bottomScrollRef}
          onScroll={handleBottomScroll}
          component={Paper} 
          variant="outlined"
          sx={{
            // Add top scrollbar when content overflows horizontally
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#c1c1c1',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#a8a8a8',
              },
            },
            // Ensure horizontal scrollbar appears on top and bottom
            overflowX: 'auto',
            // Add custom scrollbar styling for better visibility
            scrollbarWidth: 'thin',
            scrollbarColor: '#c1c1c1 #f1f1f1',
          }}
        >
          <Table sx={{ 
            tableLayout: 'fixed',
            minWidth: '100%',
            '& tbody tr': {
              position: 'relative'
            }
          }}>
            <TableHead>
              <TableRow sx={{ 
                backgroundColor: '#f5f5f5',
                '& th': {
                  fontWeight: 600,
                  borderBottom: '2px solid #d0d0d0'
                }
              }}>
                <TableCell sx={{ width: '5%' }}>#</TableCell>
                <TableCell sx={{ width: '25%' }}>Question</TableCell>
                <TableCell sx={{ width: '15%' }}>Your Answer</TableCell>
                <TableCell sx={{ width: '15%' }}>Correct Answer</TableCell>
                <TableCell sx={{ width: '12%' }}>Confident of the answer given?</TableCell>
                <TableCell sx={{ width: '10%' }}>Score</TableCell>
                <TableCell sx={{ width: '10%' }}>Student Flag</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {resultRows?.map((row, index) => {
                const isMainWithSubs = row.isMainWithSubs;
                return (
                  <TableRow 
                    key={row.isSub ? `sub-${row.mainQuestionId}-${row.id}` : row.id} 
                    sx={{ 
                      // Style main question rows with sub-questions differently
                      ...(isMainWithSubs ? {
                        backgroundColor: '#f0f7ff',
                        borderTop: '2px solid #1976d2',
                        borderBottom: '1px solid #90caf9',
                        '& td': {
                          backgroundColor: '#f0f7ff',
                          fontWeight: 500,
                          borderBottom: '1px solid #90caf9',
                          pt: 2,
                          pb: 1.5
                        },
                        '&:hover': {
                          backgroundColor: '#e3f2fd !important',
                          '& td': {
                            backgroundColor: '#e3f2fd !important'
                          }
                        }
                      } : {
                        '&:nth-of-type(even)': { 
                          backgroundColor: '#f8f9fa',
                          '& td': {
                            backgroundColor: '#f8f9fa'
                          }
                        },
                        '&:nth-of-type(odd)': { 
                          backgroundColor: '#ffffff',
                          '& td': {
                            backgroundColor: '#ffffff'
                          }
                        },
                        '&:hover': {
                          backgroundColor: '#e3f2fd !important',
                          '& td': {
                            backgroundColor: '#e3f2fd !important'
                          },
                          transition: 'background-color 0.2s ease-in-out'
                        }
                      }),
                      '&:last-child td, &:last-child th': { 
                        border: 0 
                      },
                      borderBottom: isMainWithSubs ? '1px solid #90caf9' : '1px solid #e0e0e0',
                      '& td': {
                        borderBottom: isMainWithSubs ? '1px solid #90caf9' : '1px solid #e0e0e0'
                      }
                    }}
                  >
                    <TableCell>
                      {isMainWithSubs ? (
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          {row.questionNo}
                        </Typography>
                      ) : (
                        row.questionNo
                      )}
                    </TableCell>
                    <TableCell>
                      {isMainWithSubs && (
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>
                          Main Question:
                        </Typography>
                      )}
                      <MathRenderer 
                        content={row.text || ''} 
                        variant="body2" 
                        sx={{ 
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          lineHeight: 1.4,
                          fontWeight: isMainWithSubs ? 500 : 'normal'
                        }} 
                      />
                      {!row.isSub && (row.question?.image_proxy_url || row.question?.image_url) && (
                        <Box mt={1}>
                          <img 
                            src={proxyImgUrl(row.question.image_proxy_url || row.question.image_url)} 
                            alt="question" 
                            style={{ maxWidth: '100%', height:'auto', borderRadius:4 }} 
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </Box>
                      )}
                      {/* Display answer options for multiple choice questions */}
                      {Array.isArray(row.options) && row.options.length > 0 && (
                        <Box mt={2}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Options:
                          </Typography>
                          <Box component="ol" sx={{ pl: 2, m: 0 }}>
                            {row.options.map((option, optIdx) => {
                              // Handle different option formats
                              let optionText, optionId;
                              if (typeof option === 'object' && option !== null) {
                                optionId = option.id || option.option_id || (optIdx + 1);
                                optionText = option.text || option.option_text || option.answer_text || option.label || `Option ${optionId}`;
                              } else {
                                optionId = optIdx + 1;
                                optionText = String(option);
                              }
                              
                              const isCorrect = String(optionText) === String(row.correct_answer) ||
                                               String(optionId) === String(row.correct_answer);

                              // Some options have their position baked into the stored text
                              // ("1.length only") which doubles up with the list numbering.
                              // Strip the prefix only when it matches this option's position and
                              // is not part of a real number (e.g. "3.14" stays intact).
                              const displayText = String(optionText).replace(
                                new RegExp(`^\\s*${optIdx + 1}\\s*[.)]\\s*(?=\\D|$)`),
                                ''
                              ) || String(optionText);

                              return (
                                <li key={optIdx} style={{ marginBottom: 4 }}>
                                  <Box sx={{
                                    backgroundColor: isCorrect ? 'rgba(76,175,80,0.25)' : 'transparent',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    border: isCorrect ? '1px solid rgba(76,175,80,0.5)' : 'none'
                                  }}>
                                    <Typography variant="body2" component="span">{optionId}. </Typography>
                                    <MathRenderer content={displayText} variant="body2" />
                                    {isCorrect && (
                                      <CheckCircle sx={{ fontSize: 16, ml: 0.5, color: 'success.main' }} />
                                    )}
                                  </Box>
                                </li>
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                      {!row.isSub && row.question?.points > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          ({row.question.points} point{row.question.points !== 1 ? 's' : ''})
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {isMainWithSubs ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          See sub-questions below
                        </Typography>
                      ) : (
                        <Box sx={{
                          display: 'inline-block',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          maxWidth: '100%',
                          bgcolor: parseFloat(row.marks_obtained) > 0 ? 'rgba(76,175,80,0.10)' : 'rgba(244,67,54,0.08)',
                        }}>
                          <MathRenderer content={renderAnswer(row.question || { question_type: '', options: [] }, row.user_answer)} variant="body2" />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {isMainWithSubs ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          See sub-questions below
                        </Typography>
                      ) : (
                        <Box sx={{
                          display: 'inline-block',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          maxWidth: '100%',
                          bgcolor: 'rgba(25,118,210,0.08)',
                        }}>
                          <MathRenderer content={renderCorrectAnswer(row.question || { correct_answer: row.correct_answer, question_type: row.question_type, options: row.options })} variant="body2" />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {isMainWithSubs ? '—' : (
                        <Chip
                          label={String(row.confidence ?? row.question?.confidence_level ?? 'N/A')}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', fontWeight: 600 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {isMainWithSubs ? '—' : (
                        row.marks_obtained != null && row.marks_obtained !== '' ? (
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: parseFloat(row.marks_obtained) > 0 ? 'success.main' : 'error.main' }}
                          >
                            {String(row.marks_obtained)}
                          </Typography>
                        ) : '—'
                      )}
                    </TableCell>
                    <TableCell>
                    {isTeacher ? (
                      <StudentFlagViewer attemptId={id} questionId={row.mainQuestionId} revisionData={revisionData} />
                    ) : (
                      <RevisionNoteEditor
                        attemptId={id}
                        questionId={row.mainQuestionId}
                        userRole="STUDENT"
                        mode="note"
                        revisionData={revisionData}
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
              })}
            </TableBody>
          </Table>
        </TableContainer>
          </>
        )}
    </Box>
    <Snackbar
      open={snackbarOpen}
      autoHideDuration={3000}
      onClose={() => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setSnackbarOpen(false)}
        severity={snackbarSeverity}
        sx={{ width: '100%' }}
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <PdfAnnotationViewer
        open={reviewDialog.open}
        onClose={async () => {
          setReviewDialog({ open: false, uploadId: null, filename: null, uploadSource: 'student' });
          // Refresh results after grading to update scores (skip navigation check to stay on page)
          if (isTeacher) {
            // Wait for backend to process all updates
            await new Promise(resolve => setTimeout(resolve, 500));
            // Refresh without navigation check to stay on current page
            await refreshResults(true);
          }
        }}
        uploadId={reviewDialog.uploadId}
        filename={reviewDialog.filename}
        uploadSource={reviewDialog.uploadSource || 'student'}
        role={isTeacher ? 'teacher' : 'student'}
        readOnlyAnnotations={!isTeacher}
        gradingDone={isMarked}
        attemptId={id}
        questions={questions || []}
        revisionData={revisionData || []}
        currentUserId={user?.id}
      />

      <UploadModal
        open={worksheetDialog.open}
        onClose={() => {
          const wasManagedRosterWorksheet = !!worksheetDialog.studentWorksheetUiForManagedRoster;
          setWorksheetDialog({
            open: false,
            testId: null,
            studentId: null,
            teacherManagedStudentId: null,
            attemptId: null,
            questions: [],
            revisionData: [],
            studentWorksheetUiForManagedRoster: false,
          });
          if (isTeacher && wasManagedRosterWorksheet) {
            setTimeout(() => {
              refreshResults(true);
            }, 400);
          }
        }}
        testId={worksheetDialog.testId}
        studentId={worksheetDialog.studentId}
        teacherManagedStudentId={worksheetDialog.teacherManagedStudentId}
        studentWorksheetUiForManagedRoster={!!worksheetDialog.studentWorksheetUiForManagedRoster}
        role={isTeacher ? 'teacher' : 'student'}
        readOnly={!worksheetDialog.studentWorksheetUiForManagedRoster}
        canAnnotate={isTeacher}
        allowTeacherUpload={isTeacher && !worksheetDialog.studentWorksheetUiForManagedRoster}
        showTeacherUploads={!!worksheetDialog.studentId}
        title={worksheetDialog.studentWorksheetUiForManagedRoster ? 'Worksheet and Instruction' : 'Review Worksheet'}
        attemptId={worksheetDialog.attemptId}
        questions={worksheetDialog.questions}
        revisionData={worksheetDialog.revisionData}
        currentUserId={user?.id}
      />

      {/* Reference Upload Dialog */}
      <Dialog open={referenceUploadDialogOpen} onClose={() => !referenceUploading && setReferenceUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Upload Reference Document
          <IconButton onClick={() => !referenceUploading && setReferenceUploadDialogOpen(false)} disabled={referenceUploading} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
            {/* Existing Reference Files */}
            {loadingReferenceUploads ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : referenceUploads.length > 0 ? (
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Reference Documents
                </Typography>
                <List dense>
                  {referenceUploads.map((upload) => (
                    <ListItem
                      key={upload.id}
                      divider
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={async () => {
                            if (!window.confirm('Delete this reference document?')) return;
                            try {
                              await uploadService.deleteUpload(upload.id, 'teacher');
                              setReferenceUploads(prev => prev.filter(u => u.id !== upload.id));
                              setSnackbarMessage('Reference document deleted');
                              setSnackbarSeverity('success');
                              setSnackbarOpen(true);
                            } catch (err) {
                              setSnackbarMessage(err?.response?.data?.detail || 'Delete failed');
                              setSnackbarSeverity('error');
                              setSnackbarOpen(true);
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                      button
                      component="a"
                      href={upload.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ListItemText
                        primary={upload.filename.replace(`reference_${testId}_`, '')}
                        secondary={`${(upload.size / (1024 * 1024)).toFixed(2)} MB · ${new Date(upload.uploadedAt || upload.uploaded_at).toLocaleString()}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No reference documents uploaded yet.
              </Typography>
            )}
            
            <Divider sx={{ my: 1 }} />
            
            {/* Upload New File */}
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
              disabled={referenceUploading}
            >
              Choose File
              <input
                hidden
                type="file"
                accept={ALLOWED_FILE_TYPES.join(',')}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  
                  const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
                  if (!ALLOWED_FILE_TYPES.includes(ext)) {
                    setSnackbarMessage(`File type not allowed. Allowed types: ${ALLOWED_FILE_TYPES.join(', ')}`);
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                    return;
                  }
                  
                  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                    setSnackbarMessage(`File size exceeds ${MAX_FILE_SIZE_MB} MB limit`);
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                    return;
                  }
                  
                  setReferenceUploadFile(file);
                  e.target.value = '';
                }}
              />
            </Button>
            {referenceUploadFile && (
              <Typography variant="body2" color="text.secondary">
                Selected: {referenceUploadFile.name}
                <br />
                Will be uploaded as: reference_{testId}_{referenceUploadFile.name}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !referenceUploading && setReferenceUploadDialogOpen(false)} disabled={referenceUploading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!referenceUploadFile || !studentId || !testId) return;
              
              setReferenceUploading(true);
              try {
                // Create new File with prefixed name
                const prefixedName = `reference_${testId}_${referenceUploadFile.name}`;
                const prefixedFile = new File([referenceUploadFile], prefixedName, { type: referenceUploadFile.type });
                
                await uploadService.uploadFile({
                  studentId,
                  testId,
                  file: prefixedFile,
                  role: 'teacher',
                });
                
                setSnackbarMessage('Reference document uploaded successfully');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
                setReferenceUploadFile(null);
                
                // Refresh the uploads list
                const uploads = await uploadService.listUploads({ studentId, testId, role: 'teacher' });
                const referenceFiles = uploads.filter(u => 
                  u.filename && u.filename.startsWith(`reference_${testId}_`)
                );
                setReferenceUploads(referenceFiles);
              } catch (err) {
                setSnackbarMessage(err?.response?.data?.detail || err?.message || 'Upload failed');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
              } finally {
                setReferenceUploading(false);
              }
            }}
            disabled={!referenceUploadFile || referenceUploading}
            startIcon={referenceUploading ? <CircularProgress size={16} /> : <CloudUpload />}
          >
            {referenceUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TestResultsPage;
